import React, { useCallback, useEffect, useState } from 'react';
import { CloseOutlined, MinusOutlined } from '@ant-design/icons';
import { Alert, Button, Drawer, Empty, Skeleton } from 'antd';
import { useTranslate } from '@/translations';
import { useComms } from '../context/CommsContext';
import { useRoomTimeline } from '../hooks/useRoomTimeline';
import { useCall } from '../call/CallContext';
import type { MediaSendProgressHandler, VoiceNote } from '../runtime/contracts';
import { ChatConversation } from './ChatConversation';
import './ChatDrawer.scss';

interface ChatDrawerProps {
  operator: { userId: number; displayName?: string } | undefined;
  open: boolean;
  onMinimize(): void;
  onClose(): void;
  onVisibilityChange?(open: boolean): void;
}

const initialsFor = (displayName: string) =>
  displayName
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

export const ChatDrawer = ({
  operator,
  open,
  onMinimize,
  onClose,
  onVisibilityChange,
}: ChatDrawerProps) => {
  const { t } = useTranslate();
  const {
    status,
    matrixUserId,
    directRooms,
    ensureDirectRoom,
    selectRoom,
    markLatestVisible,
    clearLatestVisible,
    sendText,
    retryMessage,
    sendImage,
    sendVoice,
  } = useComms();
  const { startCall } = useCall();
  const [resolvedRoom, setResolvedRoom] = useState<{
    operatorId: number;
    roomId: string;
  } | null>(null);
  const [resolvingRoom, setResolvingRoom] = useState(false);
  const [errorKey, setErrorKey] = useState<
    | 'commsConversationUnavailable'
    | 'commsMessageSendFailed'
    | 'commsImageSendFailed'
    | null
  >(null);
  const operatorId = operator?.userId;
  const mappedRoomId = operatorId ? directRooms.get(operatorId) : undefined;
  const roomId =
    mappedRoomId ??
    (resolvedRoom && resolvedRoom.operatorId === operatorId
      ? resolvedRoom.roomId
      : null);
  const timeline = useRoomTimeline(roomId);
  const runtimeReady = status === 'ready';
  const displayName =
    operator?.displayName ??
    (operatorId ? `${t('ccOperator')} ${operatorId}` : t('commsConversation'));

  useEffect(() => {
    setResolvedRoom(null);
    setErrorKey(null);
  }, [open, operatorId]);

  useEffect(() => {
    if (!open || !operatorId || !runtimeReady || roomId) return;
    let active = true;
    setResolvingRoom(true);
    setErrorKey(null);
    void ensureDirectRoom(operatorId)
      .then((ensuredRoomId) => {
        if (active)
          setResolvedRoom({
            operatorId,
            roomId: ensuredRoomId,
          });
      })
      .catch(() => {
        if (active) setErrorKey('commsConversationUnavailable');
      })
      .finally(() => {
        if (active) setResolvingRoom(false);
      });
    return () => {
      active = false;
    };
  }, [ensureDirectRoom, open, operatorId, roomId, runtimeReady]);

  useEffect(() => {
    if (!open || !roomId) return;
    selectRoom(roomId);
    return () => selectRoom(null);
  }, [open, roomId, selectRoom]);

  const retry = useCallback(
    (eventId: string) => {
      if (!roomId) return;
      setErrorKey(null);
      void retryMessage(roomId, eventId).catch(() =>
        setErrorKey('commsMessageSendFailed'),
      );
    },
    [retryMessage, roomId],
  );

  const submitText = useCallback(
    async (body: string) => {
      if (!roomId || !runtimeReady) return;
      setErrorKey(null);
      try {
        await sendText(roomId, body);
      } catch {
        setErrorKey('commsMessageSendFailed');
      }
    },
    [roomId, runtimeReady, sendText],
  );

  const submitImage = useCallback(
    async (file: File, onProgress: MediaSendProgressHandler) => {
      if (!roomId || !runtimeReady)
        throw new Error('Conversation is unavailable.');
      setErrorKey(null);
      try {
        await sendImage(roomId, file, onProgress);
      } catch (error) {
        setErrorKey('commsImageSendFailed');
        throw error;
      }
    },
    [roomId, runtimeReady, sendImage],
  );

  const submitVoice = useCallback(
    (voiceNote: VoiceNote, onProgress: MediaSendProgressHandler) => {
      if (!roomId || !runtimeReady)
        return Promise.reject(new Error('Conversation is unavailable.'));
      return sendVoice(roomId, voiceNote, onProgress);
    },
    [roomId, runtimeReady, sendVoice],
  );

  return (
    <Drawer
      title={
        <div className="comms-chat-header">
          <div className="comms-chat-header__avatar" aria-hidden="true">
            {initialsFor(displayName)}
          </div>
          <div className="comms-chat-header__identity">
            <div className="comms-chat-header__name-row">
              <span className="comms-chat-header__name">{displayName}</span>
              <span className="comms-chat-header__online" aria-hidden="true" />
            </div>
            <span className="comms-chat-header__subtitle">
              {t('commsConnectedFromTmsMobile')}
            </span>
          </div>
          <div className="comms-chat-header__actions">
            <Button
              type="text"
              icon={<MinusOutlined />}
              aria-label={t('commsMinimizeConversation')}
              onClick={onMinimize}
            />
            <Button
              type="text"
              icon={<CloseOutlined />}
              aria-label={t('commsCloseConversation')}
              onClick={onClose}
            />
          </div>
        </div>
      }
      open={open}
      onClose={onClose}
      afterOpenChange={onVisibilityChange}
      width={420}
      mask={false}
      closable={false}
      rootStyle={{ top: 96 }}
      className="comms-chat-drawer"
    >
      <div className="comms-chat-layout">
        {!roomId && (resolvingRoom || (open && runtimeReady && operator)) ? (
          <div className="comms-chat-loading" role="status" aria-busy="true">
            <div className="comms-chat-loading__label">
              <span>{t('commsConversationLoading')}</span>
            </div>
            <div className="comms-chat-loading__messages" aria-hidden="true">
              <Skeleton.Input
                active
                block
                className="comms-chat-loading__message"
              />
              <Skeleton.Input
                active
                block
                className="comms-chat-loading__message comms-chat-loading__message--own"
              />
              <Skeleton.Input
                active
                block
                className="comms-chat-loading__message comms-chat-loading__message--short"
              />
            </div>
            <div className="comms-chat-loading__composer" aria-hidden="true">
              <Skeleton.Avatar active size={34} />
              <Skeleton.Input
                active
                block
                className="comms-chat-loading__composer-input"
              />
              <Skeleton.Button active size="large" shape="round" />
            </div>
          </div>
        ) : open && roomId ? (
          <div className="comms-chat-conversation">
            {timeline.messages.length === 0 && (
              <Empty description={t('commsNoMessages')} />
            )}
            <ChatConversation
              key={roomId}
              messages={timeline.messages}
              matrixUserId={matrixUserId}
              peerDisplayName={operator?.displayName ?? ''}
              runtimeReady={runtimeReady}
              hasMore={timeline.hasMore}
              isLoadingOlder={timeline.isLoadingOlder}
              loadOlder={timeline.loadOlder}
              onSendText={submitText}
              onRetry={retry}
              onSendImage={submitImage}
              onSendVoice={submitVoice}
              onStartCall={() =>
                operatorId ? startCall(roomId, operatorId) : Promise.resolve()
              }
              onLatestVisible={(eventId) => markLatestVisible(roomId, eventId)}
              onLatestHidden={(eventId) => clearLatestVisible(roomId, eventId)}
            />
          </div>
        ) : null}
        {errorKey && <Alert role="alert" type="error" message={t(errorKey)} />}
      </div>
    </Drawer>
  );
};
